import { useStore } from "@/stores/useStore";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import { Loading } from "@/components/Page/Loading";
import { Forbidden } from "@/components/Page/Forbidden";
import { useRoles } from "@/hooks/useRoles";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/Base/Tabs";
import { ShopInfo } from "@/components/Settings/Shop/ShopInfo";
import { ShopSettingsForm } from "@/components/Settings/Shop/ShopSettingsForm";
import { TerminologySettings } from "@/components/Settings/Shop/TerminologySettings";
import { FEATURE_FLAGS, Shop } from "@/types/v2";
import { shop as shopRepository } from "@/repositories/shop";

const CompanyInfoPage = () => {
  const { isSuperAdmin, isAdmin, isOffice, isInitialized } = useRoles();
  useIsAuthenticated();
  const shopData = useStore((s) => s.shop);
  const shopFirestoreId = useStore((s) => s.shopId);
  const syncWithSource = useStore((s) => s.syncWithSource);

  const handleShopUpdate = async (update: Partial<Shop>) => {
    if (!shopFirestoreId) return;
    // Firestore updateDoc replaces whole maps — merge shopConfig onto the live
    // value so a partial form save can't wipe siblings (workingHours, terminology…).
    const next: Partial<Shop> = update.shopConfig
      ? {
          ...update,
          shopConfig: { ...shopData?.shopConfig, ...update.shopConfig },
        }
      : update;
    await shopRepository.update(shopFirestoreId, next);
  };

  const handleFeatureToggle = async (
    feature: FEATURE_FLAGS,
    enabled: boolean
  ) => {
    if (!shopFirestoreId || !shopData) return;
    await shopRepository.update(shopFirestoreId, {
      enabledFeatures: { ...shopData.enabledFeatures, [feature]: enabled },
    });
  };

  if (!isInitialized) return <Loading />;
  if (isInitialized && !(isSuperAdmin || isAdmin || isOffice))
    return <Forbidden />;

  if (!shopData || !shopFirestoreId) {
    return (
      <div className="mx-auto max-w-screen-xl flex-grow p-5">
        <h1 className="text-2xl">Shop Info</h1>
        <div className="flex flex-row items-center justify-between p-4">
          <Loading />
        </div>
      </div>
    );
  }
  return (
    <div className="mx-auto w-full max-w-screen-xl flex-grow p-5">
      <h1 className="text-2xl">Shop Settings</h1>
      <div className="flex flex-row items-start justify-between gap-2 p-4">
        <Tabs defaultValue="shopInfo" className="w-full">
          <TabsList variant={"primary-alt"}>
            <TabsTrigger value="shopInfo" variant={"primary-alt"}>
              Shop Info
            </TabsTrigger>
            <TabsTrigger value="configuration" variant={"primary-alt"}>
              Configuration
            </TabsTrigger>
            <TabsTrigger value="terminology" variant={"primary-alt"}>
              Terminology
            </TabsTrigger>
          </TabsList>
          <TabsContent value="shopInfo">
            <div className="rounded-xl border border-primary-foreground p-4">
              <div className="flex max-w-screen-sm flex-col gap-4">
                <ShopInfo />
              </div>
            </div>
          </TabsContent>
          <TabsContent value="configuration">
            <div className="rounded-xl border border-primary-foreground p-4">
              <div className="flex flex-col gap-4">
                <ShopSettingsForm
                  shop={shopData}
                  onSubmit={(shopConfig) => handleShopUpdate({ shopConfig })}
                  onFeatureToggle={handleFeatureToggle}
                />
              </div>
            </div>
          </TabsContent>
          <TabsContent value="terminology">
            <div className="rounded-xl border border-primary-foreground p-4">
              <div className="flex max-w-screen-sm flex-col gap-4">
                <TerminologySettings
                  shopData={shopData}
                  syncWithSource={syncWithSource}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CompanyInfoPage;
